import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Linkedin } from "lucide-react";
import landingStyles from "./LandingPage.module.css";
import pricingStyles from "./PricingPage.module.css";
import styles from "./AboutPage.module.css";
import SiteFooter from "./SiteFooter";
import MarketingHeader from "./MarketingHeader";
import profileParamita from "../assets/profilephotos/tina.jfif";
import profileAngkon from "../assets/profilephotos/angkon.jpg";
import { CONTACT_EMAIL } from "../constants/site";
import { openContactModal } from "./ContactModal";
/** About page copy per marketing spec (About Page — RemiMinderAI). */
const AboutPage = () => {
  const navigate = useNavigate();
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const techStack = [
    {
      id: "mobile",
      name: "Mobile Interface",
      bio: "Built with Flutter for a high-performance, cross-platform experience (iOS/Android) with rapid feature parity across devices.",
    },
    {
      id: "docintel",
      name: "Document Intelligence",
      bio: "Google ML Kit for on-device OCR and Google Document AI for high-accuracy extraction of prescriptions, lab results, and complex medical documents.",
    },
    {
      id: "genai",
      name: "Generative AI",
      bio: "Powered by Google Gemini via Vertex AI for real-time visit transcription, medical summarization, and personalized health action plans.",
    },
  ];

  const cloudInfrastructure = [
    {
      id: "be",
      name: "Backend",
      bio: "High-speed FastAPI (Python) services hosted on Google Cloud Run for serverless, auto-scaling performance.",
    },
    {
      id: "db",
      name: "Database",
      bio: "Google Cloud SQL (PostgreSQL) for robust, relational storage of patient records with automated backups.",
    },
    {
      id: "sec",
      name: "Security & Compliance",
      bio: "RemiMinderAI is built on Google Cloud's secure infrastructure with enterprise-grade encryption. We collect only the minimum information needed to use the app — your name, optional phone number, and email. We never ask for medical record numbers, dates of birth, or social security numbers. Recordings belong to you, and you choose whether to share them with a family caregiver.",
    },
  ];

  const dataControlItems = [
    "Encrypted. Recordings and summaries are encrypted in storage and in transit.",
    "Yours to share. You control whether to share with a caregiver.",
    "Minimal data. We don't ask for medical record numbers, dates of birth, or SSNs.",
    "Sign in with Google. No separate password to manage.",
    "No selling, no ads. We don't sell user data. We don't serve ads. Our business model is the product, not the people using it.",
    "You control who sees what. Patients can grant full or partial access to caregivers and revoke it at any time. You decide what's shared, with whom, and for how long.",
  ];

  const roadmapItems = [
    "Provider documentation pilot and care team collaboration",
    "Healthcare organization integrations and API platform",
    "Multilingual voice support",
  ];

  const companyLines = [
    "Company Name: RemiMinderAI",
    "Industry: Digital Health / HealthTech",
    "Company Size: 2–10 employees",
    "Headquarters: Hollister, California",
    "Founded: 2025",
    "Email: team@remiminderai.com",
  ];

  return (
    <div className={`${landingStyles.container} ${styles.page}`}>
      <MarketingHeader
        scrolled={scrolled}
        headerExtraClass={pricingStyles.pricingHeader}
      />

      <section className={`${styles.section} ${styles.altWhite}`}>
        <div className={styles.sectionNarrow}>
          <h1 className={styles.heroTitle}>About RemiMinderAI</h1>
          <p className={styles.heroSub}>
            Healthcare coordination, finally built for the people doing the coordinating.
          </p>
          <div className={styles.prose}>
            <p>
              RemiMinderAI is an AI-native health management platform that helps families capture,
              organize, and act on medical information so critical care moments never slip through the
              cracks.
            </p>
            <p>
              We turn fragmented doctor visits, medication schedules, and scattered records into a single,
              shared source of truth for patients and the caregivers who support them.
            </p>
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altCream}`}>
        <div className={styles.sectionNarrow}>
          <h2 className={styles.sectionHeading}>Why We Built This</h2>
          <div className={styles.prose}>
            <p>
              Anyone who has helped a parent, partner, or child navigate the healthcare system knows the
              feeling: you leave the doctor&apos;s office already forgetting half of what was said,
              juggling prescription names you can&apos;t pronounce, and wondering who else in the family
              needs to know what just happened.
            </p>
            <p>
              RemiMinderAI started with that exact problem. Care doesn&apos;t happen in a single
              appointment — it happens in the days and weeks after, across phone calls, pharmacies, and
              kitchen tables. We&apos;re building the platform that holds it all together.
            </p>
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altWhite}`}>
        <div className={styles.sectionWide}>
          <div className={styles.twoCol}>
            <div className={`${landingStyles.stepCard} ${styles.colCard}`}>
              <h2 className={styles.sectionHeading} style={{ fontSize: "1.65rem", marginBottom: "12px" }}>
                What We Do
              </h2>
              <div className={styles.prose}>
                <p>
                  RemiMinderAI is an AI-powered health management platform that helps patients record,
                  organize, and share their health information with caregivers and healthcare providers.
                </p>
                <p>
                  Our mobile and web application uses advanced speech recognition and natural language
                  processing to transcribe doctor visits, extract key medical information, and generate
                  actionable health insights.
                </p>
                <p>
                  The platform connects patients with their care team in real time, turning every
                  appointment into structured, shareable knowledge instead of forgotten conversations.
                </p>
              </div>
            </div>
            <div className={`${landingStyles.stepCard} ${styles.colCard}`}>
              <h2 className={styles.sectionHeading} style={{ fontSize: "1.65rem", marginBottom: "12px" }}>
                Problems We Solve
              </h2>
              <ul className={styles.problemList}>
                <li>
                  Patients forget 40–80% of medical information immediately after a visit, and nearly half
                  of what&apos;s remembered is recalled incorrectly
                  <sup>1</sup>
                </li>
                <li>
                  Caregivers lack real-time visibility into their loved ones&apos; health status and
                  appointments
                </li>
                <li>
                  Fragmented health records across multiple providers create dangerous gaps in care
                </li>
                <li>Seniors struggle with complex medication schedules and adherence</li>
                <li>
                  Families waste hours coordinating care through scattered phone calls, texts, and sticky
                  notes
                </li>
              </ul>
              <p className={styles.footnote}>
                <sup>1</sup> Kessels, R.P.C. Patients&apos; memory for medical information. Journal of the
                Royal Society of Medicine, 2003.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altCream}`}>
        <div className={styles.sectionNarrow}>
          <h2 className={styles.sectionHeading}>Your Data, Your Control</h2>
          <p className={styles.prose} style={{ marginBottom: "16px" }}>
            Healthcare data is the most sensitive information a person shares. We treat it that way.
          </p>
          <ul className={styles.dataList}>
            {dataControlItems.map((text) => (
              <li key={text.slice(0, 40)}>
                <span className={styles.dataBullet} aria-hidden="true">
                  •
                </span>
                <span>{text}</span>
              </li>
            ))}
          </ul>
          <Link to="/privacy" className={styles.privacyLink}>
            Read our full Privacy Policy →
          </Link>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altWhite}`}>
        <div className={styles.sectionWide}>
          <div className={styles.centerTitle}>
            <h2 className={styles.sectionHeading}>Our Core Tech Stack</h2>
          </div>
          <div className={styles.cardGrid}>
            {techStack.map((item) => (
              <div key={item.id} className={landingStyles.stepCard}>
                <h3>{item.name}</h3>
                <p>{item.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altCream}`}>
        <div className={styles.sectionWide}>
          <div className={styles.centerTitle}>
            <h2 className={styles.sectionHeading}>Cloud Infrastructure</h2>
          </div>
          <div className={styles.cardGrid}>
            {cloudInfrastructure.map((item) => (
              <div key={item.id} className={landingStyles.stepCard}>
                <h3>{item.name}</h3>
                <p>{item.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altWhite}`}>
        <div className={styles.sectionWide}>
          <div className={styles.centerTitle}>
            <h2 className={styles.sectionHeading}>Our Team</h2>
          </div>
          <div className={styles.teamGrid}>
            <div className={`${landingStyles.personaCard} ${styles.teamCardInner}`}>
              <div className={styles.avatar}>
                <img
                  src={profileParamita}
                  alt="Paramita (Tina) Malakar"
                  className={styles.avatarImg}
                />
              </div>
              <h3 className={styles.teamName}>Paramita (Tina) Malakar</h3>
              <p className={styles.teamRole}>Founder &amp; CEO</p>
              <p className={styles.teamBio}>
                18+ years in enterprise delivery across federal IT and HealthTech. Built RemiMinderAI
                solo from idea to beta in under 6 months.
              </p>
              <p className={styles.teamCreds}>
                BS Business Management &amp; E-Global Business · PMP · CSM · AIPM · Security+ CE
              </p>
              <a
                href="https://www.linkedin.com/in/paramitam/?skipRedirect=true"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkedinLink}
              >
                <Linkedin size={16} strokeWidth={1.75} />
                <span>LinkedIn →</span>
              </a>
            </div>

            <div className={`${landingStyles.personaCard} ${styles.teamCardInner}`}>
              <div className={styles.avatar}>
                <img src={profileAngkon} alt="Angkon Biswas" className={styles.avatarImg} />
              </div>
              <h3 className={styles.teamName}>Angkon Biswas</h3>
              <p className={styles.teamRole}>Co-Founder &amp; Mobile Engineering Lead</p>
              <p className={styles.teamBio}>
                Building and shipping the full mobile stack across iOS and Android using Flutter,
                Firebase, and GCP. Background in cloud infrastructure, IT operations, and agile
                delivery.
              </p>
              <p className={styles.teamCreds}>
                MBA in Systems · MS Cloud Computing · Azure Fundamentals · Google Advanced Data
                Analytics · Scrum Master · 6x Databricks Certified
              </p>
              <a
                href="https://www.linkedin.com/in/angkon-biswas-a13167214/"
                target="_blank"
                rel="noopener noreferrer"
                className={styles.linkedinLink}
              >
                <Linkedin size={16} strokeWidth={1.75} />
                <span>LinkedIn →</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altCream}`}>
        <div className={styles.sectionWide}>
          <div className={styles.centerTitle}>
            <h2 className={styles.sectionHeading}>What&apos;s Next</h2>
          </div>
          <ul className={styles.roadmapList}>
            {roadmapItems.map((line) => (
              <li key={line} className={styles.roadItem}>
                <span className={styles.dataBullet} aria-hidden="true">
                  •
                </span>
                <span>{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altWhite}`}>
        <div className={styles.sectionNarrow}>
          <div className={landingStyles.stepCard}>
            <h2 className={styles.sectionHeading}>Company Information</h2>
            <ul className={styles.companyList}>
              {companyLines.map((line) => (
                <li key={line}>
                  <span className={styles.companyBullet} aria-hidden="true">
                    •
                  </span>
                  <span>{line}</span>
                </li>
              ))}
              <li>
                <span className={styles.companyBullet} aria-hidden="true">
                  •
                </span>
                <span>
                  LinkedIn:{" "}
                  <a
                    href="https://www.linkedin.com/company/remiminderai/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    linkedin.com/company/remiminderai
                  </a>
                </span>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section className={`${styles.section} ${styles.altCream}`}>
        <div className={styles.sectionNarrow} style={{ textAlign: "center" }}>
          <p className={styles.prose} style={{ marginBottom: "22px" }}>
            Want early access? We&apos;re onboarding a limited group of families and caregivers during private
            beta. If RemiMinderAI sounds like something you or someone you love needs, request an invite.
          </p>
          <div className={styles.ctaRow}>
            <button
              type="button"
              className={landingStyles.androidButton}
              onClick={() => openContactModal()}
            >
              Contact the Team →
            </button>
          </div>
        </div>
      </section>

      <div className={styles.backHomeWrap}>
        <button type="button" className={styles.backHome} onClick={() => navigate("/")}>
          ← Back to Home
        </button>
      </div>

      <SiteFooter />
    </div>
  );
};

export default AboutPage;
